import { CRMChannel } from "@jugl-web/rest-api";
import { PlainButton, Tab, Tooltip } from "@jugl-web/ui-kit";
import { cx, useAppVariant, useTranslations } from "@jugl-web/utils";
import { FC, useMemo } from "react";
import { collapsedIconButtonClassName } from "../../consts";
import { useIsInboxSinglePane } from "../../hooks/useIsInboxSinglePane";
import { useClientSupportPageContext } from "../../providers/ClientSupportPageProvider/useClientSupportPageContext";
import { ReactComponent as BackIcon } from "./assets/back.svg";
import { ReactComponent as CloseIcon } from "./assets/close.svg";
import { CustomerTabContent } from "./components/CustomerTabContent";
import { HistoryTabContent } from "./components/HistoryTabContent";
import { TasksTabContent } from "./components/TasksTabContent";
import { TicketsTabContent } from "./components/TicketsTabContent";
import { ConversationSidePanelTab } from "./types";

export const ConversationSidePanel: FC = () => {
  const { t } = useTranslations();
  const { isCx } = useAppVariant();
  const {
    conversationSidePanelVariant,
    openConversationSidePanel,
    selectedChannel,
    closeConversationSidePanel,
  } = useClientSupportPageContext();

  const isInboxSinglePane = useIsInboxSinglePane();

  const isWhatsappChannel = selectedChannel === CRMChannel.whatsapp;
  const isVoiceChannel = selectedChannel === CRMChannel.voice;
  const showTasksTab = !isCx && isWhatsappChannel;
  const showTicketsTab = isCx;

  const selectedTab =
    conversationSidePanelVariant ?? ConversationSidePanelTab.customer;

  const tabs = useMemo(
    () =>
      // Customer/Tickets tabs are intentionally hidden for the Phone Calls channel
      isVoiceChannel
        ? [
            {
              id: ConversationSidePanelTab.history,
              title: t({
                id: "client-support-page.history",
                defaultMessage: "History",
              }),
              onClick: () =>
                openConversationSidePanel({
                  variant: ConversationSidePanelTab.history,
                }),
            },
          ]
        : ([
            {
              id: ConversationSidePanelTab.customer,
              title: t({
                id: "client-support-page.customer",
                defaultMessage: "Customer",
              }),
              onClick: () =>
                openConversationSidePanel({
                  variant: ConversationSidePanelTab.customer,
                }),
            },
            showTicketsTab && {
              id: ConversationSidePanelTab.tickets,
              title: t({
                id: "client-support-page.tickets",
                defaultMessage: "Tickets",
              }),
              onClick: () =>
                openConversationSidePanel({
                  variant: ConversationSidePanelTab.tickets,
                }),
            },
            showTasksTab && {
              id: ConversationSidePanelTab.tasks,
              title: t({
                id: "common.tasks",
                defaultMessage: "Tasks",
              }),
              onClick: () =>
                openConversationSidePanel({
                  variant: ConversationSidePanelTab.tasks,
                }),
            },
          ].filter(Boolean) as Array<{
            id: ConversationSidePanelTab;
            title: string;
            onClick: () => void;
          }>),
    [isVoiceChannel, openConversationSidePanel, showTasksTab, showTicketsTab, t]
  );

  const activeTab = useMemo(() => {
    if (isVoiceChannel) {
      return ConversationSidePanelTab.history;
    }
    if (selectedTab === ConversationSidePanelTab.tasks && !showTasksTab) {
      return ConversationSidePanelTab.customer;
    }
    if (selectedTab === ConversationSidePanelTab.tickets && !showTicketsTab) {
      return ConversationSidePanelTab.customer;
    }
    if (!isCx && selectedTab === ConversationSidePanelTab.tickets) {
      return ConversationSidePanelTab.customer;
    }
    return selectedTab;
  }, [isCx, isVoiceChannel, selectedTab, showTasksTab, showTicketsTab]);

  return (
    <div className="flex h-full w-full min-w-[360px] flex-col overflow-hidden border-0 border-l border-solid border-dark-100 bg-white max-lg:w-full max-lg:min-w-0">
      <div className="flex h-[60px] shrink-0 items-center justify-between gap-8 border-0 border-b border-solid border-dark-100 px-6 max-lg:gap-3 max-lg:px-3">
        <div className="flex h-full min-w-0 items-center gap-3">
          {isInboxSinglePane && (
            <PlainButton
              className={cx(collapsedIconButtonClassName, "h-8 w-8")}
              onClick={closeConversationSidePanel}
            >
              <BackIcon />
            </PlainButton>
          )}
          <div className="flex h-full items-end">
            {tabs.map((tab) => (
              <Tab
                key={tab.id}
                title={tab.title}
                isSelected={tab.id === activeTab}
                onClick={tab.onClick}
                className="w-auto"
                labelClassName="text-sm"
              />
            ))}
          </div>
        </div>
        {!isInboxSinglePane && (
          <Tooltip
            renderTrigger={({ props, ref }) => (
              <PlainButton
                ref={ref}
                className={cx(collapsedIconButtonClassName, "h-8 w-8")}
                {...props}
                onClick={closeConversationSidePanel}
              >
                <CloseIcon />
              </PlainButton>
            )}
          >
            {t({
              id: "client-support-page.close-info-panel",
              defaultMessage: "Close Info Panel",
            })}
          </Tooltip>
        )}
      </div>
      <div className="jugl__custom-scrollbar min-h-0 flex-1 overflow-auto">
        {activeTab === ConversationSidePanelTab.history && (
          <HistoryTabContent />
        )}
        {activeTab === ConversationSidePanelTab.customer && (
          <CustomerTabContent />
        )}
        {activeTab === ConversationSidePanelTab.tickets && showTicketsTab && (
          <TicketsTabContent />
        )}
        {activeTab === ConversationSidePanelTab.tasks && showTasksTab && (
          <TasksTabContent />
        )}
      </div>
    </div>
  );
};
